<template>
  <b-nav-item-dropdown
    v-if="canSwitchAccount"
    class="dropdown-employee-switch mr-25"
    menu-class="dropdown-menu-media"
    right
    @show="getEmployeesList"
  >
    <template #button-content>
      <feather-icon
        class="text-body"
        icon="UsersIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          Chuyển đổi tài khoản
        </h4>
      </div>
    </li>

    <!-- Search -->
    <li class="px-2 py-1">
      <b-form-input
        v-model="searchTerm"
        placeholder="Tìm kiếm cán bộ..."
        size="sm"
        @input="filterEmployees"
      />
    </li>

    <!-- Employees List -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <b-link
        v-for="employee in filteredEmployees"
        :key="employee.id"
        :class="{ 'employee-active': isActiveAccount(employee), 'employee-disabled': isActiveAccount(employee) }"
        :disabled="isActiveAccount(employee)"
        @click="onSwitchAccount(employee)"
      >
        <b-media>
          <span class="media-heading font-weight-bolder" :class="{ 'text-muted': isActiveAccount(employee) }">
            {{ employee.fullName || employee.name || '' }}
            <span
              v-if="isActiveAccount(employee)"
              class="badge badge-light-success ml-1"
            >
              Đang sử dụng
            </span>
          </span>
          <br>
          <span
            v-if="employee.username"
            class="mb-0 text-truncate text-muted small"
          >
            {{ employee.username }}
          </span>
        </b-media>
      </b-link>
      <div
        v-if="filteredEmployees.length === 0"
        class="text-center py-3 text-muted"
      >
        Không tìm thấy cán bộ
      </div>
    </vue-perfect-scrollbar>

    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
      no-wrap
      :show="isLoading"
    />
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BMedia, BLink, BOverlay, BFormInput,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/notification'
import { isUserLoggedIn, getUser, getImpersonationSignature } from '@/auth/utils'
import { isSystemAdmin } from '@/utils'

export default {
  components: {
    BOverlay,
    BNavItemDropdown,
    BMedia,
    BLink,
    BFormInput,
    VuePerfectScrollbar,
  },
  data() {
    return {
      isLoading: false,
      searchTerm: '',
      employees: [],
      user: getUser(),
    }
  },
  computed: {
    isSystemAdmin() {
      return isSystemAdmin()
    },
    impersonationSignature() {
      return getImpersonationSignature()
    },
    // Hiển thị dropdown nếu là systemAdmin hoặc đang ở chế độ impersonate
    canSwitchAccount() {
      return this.isSystemAdmin || this.impersonationSignature !== null
    },
    perfectScrollbarSettings() {
      return {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      }
    },
    filteredEmployees() {
      if (!this.searchTerm) {
        return this.employees
      }
      const term = this.searchTerm.toLowerCase()
      return this.employees.filter(emp => {
        const fullName = (emp.fullName || emp.name || '').toLowerCase()
        const username = (emp.username || '').toLowerCase()
        return fullName.includes(term) || username.includes(term)
      })
    },
  },
  methods: {
    getInitials(name) {
      if (!name) return 'CB'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    isActiveAccount(employee) {
      if (!employee.accountId || !this.user) return false
      // So sánh accountId của employee với accountId của user hiện tại
      return employee.accountId === this.user.accountId || employee.accountId === this.user.id
    },
    async getEmployeesList() {
      if (!isUserLoggedIn() || !this.canSwitchAccount) return

      this.isLoading = true
      try {
        await this.$store.dispatch('employee/getEmployeesByOrganizationId', {
          organizationId: this.user.orgId,
        })
        this.employees = this.$store.getters['employee/employees'] || []
      } catch (e) {
        showErrorToast(this, e)
      } finally {
        this.isLoading = false
      }
    },
    filterEmployees() {
      // Filter được xử lý ở computed filteredEmployees
    },
    async onSwitchAccount(employee) {
      // Không cho switch vào tài khoản đang active
      if (this.isActiveAccount(employee)) {
        return
      }

      if (!employee.accountId) {
        showWarningToast(this, 'Cán bộ này chưa có tài khoản')
        return
      }

      this.isLoading = true
      try {
        const response = await this.$store.dispatch('auth/switchAccount', {
          accountId: employee.accountId,
          signature: this.impersonationSignature,
        })
        if (response) {
          const { isSuccessful, message } = response
          if (isSuccessful) {
            showSuccessToast(this, 'Chuyển đổi tài khoản thành công')
            // Redirect về trang home và reload
            setTimeout(() => {
              window.location.href = '/'
            }, 500)
          } else {
            showErrorToast(this, message || 'Chuyển đổi tài khoản thất bại')
            this.isLoading = false
          }
        }
      } catch (e) {
        showErrorToast(this, e)
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.media {
  padding: 5px 0 5px 20px !important;
  border: none;
  border-bottom: 1px solid #ebe9f1;
}
.scrollable-container {
  max-height: 400px;
}
.employee-item .media {
  margin: 0;
}
.employee-active {
  background-color: #f0f0f0;
  cursor: not-allowed;
  opacity: 0.7;
}
.employee-disabled {
  pointer-events: none;
}
.employee-disabled:hover {
  background-color: #f0f0f0;
}
</style>
